// The frozen copy of a forecast the report renders from: the main forecast (as displayed,
// so it follows the timeline scrub), a comparison root, or a branch overlaid on the active
// forecast. Shared by the report modal and the report embedded under the timeline.

import { forecasterDisplayName, rootEngine } from "../forecast/engines.js";
import { isCompareActive } from "../comparison";

/**
 * What a report request names: `true` for the main forecast, a root (a top-level `question`
 * and no `modification`) for a comparison root, or anything else for a branch overlay.
 * Keying on `question`/`modification` rather than the in-session `_engine` stamp keeps a
 * persisted compare root (reloaded, `_engine` gone) classified as its own report source.
 * @returns {"main"|"root"|"branch"}
 */
export function reportTargetKind(val) {
  if (val === true || !val || typeof val !== "object") return "main";
  return val.question && !val.modification ? "root" : "branch";
}

/**
 * The report snapshot for `val` (see reportTargetKind), or null with no active forecast.
 * @param {true|object} val
 * @param {{active: object|null, displayActive: object|null, comparison: object}} deps
 */
export function buildReportSnapshot(val, { active, displayActive, comparison }) {
  if (!val || !active) return null;
  const kind = reportTargetKind(val);
  const isRoot = kind === "root";
  const isBranch = kind === "branch";
  let src;
  if (isBranch)
    src = {
      contract: val.contract || { ...active.contract, probability: val.probability },
      events: val.events || [],
      serverExplanation: (val.serverExplanation ?? val.serverContext?.explanation) || "",
      serverContext: val.serverContext || active.serverContext,
      serverPrediction: active.serverPrediction,
      report: val.report || null,
    };
  else if (isRoot) src = val;
  // displayActive already overlays a rewound snapshot's contract/events/report/explanation, so
  // the main report follows the scrub.
  else src = displayActive;
  const snap = JSON.parse(JSON.stringify(src));
  // Only the plain MAIN forecast (neither a compare root nor a branch overlay) is described by
  // the active run's report_pointers, so only it may be overlaid with a pointer-fetched report.
  // The report reads this flag rather than re-deriving eligibility from display-chrome markers.
  snap._overlayEligible = !isRoot && !isBranch;
  if (isBranch) snap._branchMod = val.modification || "";
  snap._reportRunId = (isRoot || isBranch ? (val.id ?? val.runId) : active.id) ?? null;
  if (isRoot) snap._engineLabel = forecasterDisplayName(rootEngine(val) || "");
  else if (!isBranch && isCompareActive(comparison))
    snap._engineLabel = forecasterDisplayName(rootEngine(active));
  return snap;
}
